import { exposed, fromNode, ViewModel } from '@rehaje/core'
import { batch, reactive } from '@rehaje/reactive'
import {
  clearTimeout,
  isEqual,
  pDebounceThrottle,
  setTimeout,
  Storage,
  toJSON
} from '@rehaje/shared'

import type { QueryPlugin } from './plugin'
import type { QueryNode } from './TypedNode'
import type {
  TypedDefinitionConstructor,
  TypedNodeConstructor
} from '@rehaje/core'
import type { TRecord } from '@rehaje/shared'

const storage = new Storage('query')

type CacheItem = {
  data: any
  timestamp: number
}

export class QueryModel extends ViewModel {
  static __r_suspense_promises = new Map<string, Promise<any>>()

  static definition?: TypedDefinitionConstructor

  static node?: TypedNodeConstructor

  __r_abort_controller?: AbortController

  __r_cache_timer?: number

  __r_loading_debounce_timer?: number

  @reactive()
  __r_network_online?: boolean

  __r_poll_count?: number

  __r_poll_timer?: number

  __r_retry_count?: number

  __r_retry_timer?: number

  __r_timeout_timer?: number

  __r_trigger_version = 0

  @reactive()
  __r_window_focused?: boolean

  @reactive()
  __r_window_visible?: boolean

  /**
   * 自动刷新，包含以下能力
   * 1. 页面首次加载自动请求
   * 2. 依赖参数变化时自动请求
   * 3. 其他自动请求配置能力/SWR能力等
   */
  @fromNode()
  @reactive()
  autoRefresh = false

  /**
   * 设置缓存数据回收时间。默认缓存数据 5 分钟后回收
   * 如果设置为 -1, 则表示缓存数据永不过期
   */
  @fromNode()
  @reactive()
  cacheTime: number = 300_000

  /**
   * 返回数据
   */
  @exposed()
  @reactive()
  data?: any = null

  /**
   * 请求防抖时间间隔
   */
  @fromNode()
  @reactive()
  debounceTime: number = 0

  /**
   * 错误信息
   */
  @exposed()
  @reactive()
  error?: any = null

  /**
   * 错误最大重试次数
   */
  @fromNode()
  @reactive()
  errorRetryCount?: number

  /**
   * 错误重试间隔时间
   */
  @fromNode()
  @reactive()
  errorRetryIntervalTime?: number

  /**
   * 请求轮询次数
   */
  @fromNode()
  @reactive()
  intervalCount: number = 0

  /**
   * 请求轮询时间间隔
   */
  @fromNode()
  @reactive()
  intervalTime: number = 0

  /**
   * 正在加载中
   */
  @exposed()
  @reactive()
  loading = false

  /**
   * loading 的防抖时间间隔
   */
  @fromNode()
  @reactive()
  loadingDebounceTime: number = 360

  /**
   * 失败事件
   */
  @fromNode()
  @reactive()
  onError?: (...args: any[]) => any

  /**
   * 成功事件
   */
  @fromNode()
  @reactive()
  onSuccess?: (...args: any[]) => any

  /**
   * 请求参数
   */
  @fromNode()
  @reactive('shallow')
  params?: TRecord = {}

  /**
   * 自定义 Query 请求参数配置
   */
  @fromNode()
  @reactive('deep')
  props: TRecord = {}

  /**
   * 网页隐藏时自动刷新，默认值等于 autoRefresh
   */
  @fromNode()
  @reactive()
  refreshOnHidden = true

  /**
   * 网络离线时自动刷新
   */
  @fromNode()
  @reactive()
  refreshOnOffline?: boolean

  /**
   * 网络重连时自动刷新
   */
  @fromNode()
  @reactive()
  refreshOnReconnect?: boolean

  /**
   * window 聚焦时自动刷新
   */
  @fromNode()
  @reactive()
  refreshOnWindowFocus?: boolean

  /**
   * 错误时是否支持重试
   */
  @fromNode()
  @reactive()
  shouldRetryOnError?: boolean

  /**
   * 缓存数据保持新鲜时间。在该时间间隔内，认为数据是新鲜的，不会重新发请求
   * 如果设置为 -1，则表示数据永远新鲜
   */
  @fromNode()
  @reactive()
  staleTime: number = 0

  /**
   * 请求节流时间间隔
   */
  @fromNode()
  @reactive()
  throttleTime: number = 0

  /**
   * 超时
   */
  @fromNode()
  @reactive()
  timeout: number = 10_000

  /**
   * 数据转换器
   */
  @fromNode()
  @reactive()
  transformer?: (...args: any[]) => any

  /**
   * 上次更新数据的时间戳
   */
  // @exposed()
  @reactive()
  updateAt?: null | number = null

  get __r_hash_id() {
    return `${this.plugin.props?.namespace ?? this.__r_root.id}-${this.__r_address}`
  }

  get plugin() {
    return this.getPlugin<QueryPlugin>()
  }

  /**
   * 正在轮询中
   */
  get polling() {
    return this.__r_poll_count && this.__r_poll_count > 0
  }

  /**
   * 正在重试中
   */
  get retrying() {
    return this.__r_retry_count && this.__r_retry_count > 0
  }

  /**
   * 请求触发器
   */
  @exposed()
  @reactive('computed')
  get trigger() {
    const self = this
    const trigger = pDebounceThrottle(
      (params: TRecord = {}) => {
        self.__r_trigger_version++
        const currentId = self.__r_trigger_version
        self.evaluate({ params })
        return self.__r_startTrigger(currentId)
      },
      { debounceTime: this.debounceTime, throttleTime: this.throttleTime }
    )
    return (params: TRecord = {}) => {
      return trigger(params)
    }
  }

  constructor(node: QueryNode) {
    super(node)
    this.addRenderEffect(() => {
      const onVisible = () => {
        this.__r_window_visible = document.visibilityState !== 'hidden'
      }
      const onFocus = () => {
        this.__r_window_focused = document.hasFocus()
        if (this.refreshOnWindowFocus && this.__r_window_focused) this.trigger()
      }
      const onOnline = () => {
        this.__r_network_online = navigator.onLine
        if (this.refreshOnOffline && this.__r_network_online) this.trigger()
      }
      globalThis.addEventListener('visibilitychange', onVisible)
      globalThis.addEventListener('focus', onFocus)
      globalThis.addEventListener('blur', onFocus)
      globalThis.addEventListener('online', onOnline)
      globalThis.addEventListener('offline', onOnline)
      return () => {
        globalThis.removeEventListener('visibilitychange', onVisible)
        globalThis.removeEventListener('focus', onFocus)
        globalThis.removeEventListener('blur', onFocus)
        globalThis.removeEventListener('online', onOnline)
        globalThis.removeEventListener('offline', onOnline)
        this.abort()
      }
    })
    if (this.autoRefresh) {
      this.evaluate({ params: {} })
      this.__r_initTrigger()
      this.addReaction(
        () => this.getWatchDependencies(),
        () => this.trigger(),
        { equals: isEqual }
      )
    }
  }

  /**
   * 检查新鲜度
   */
  async __r_checkStale() {
    const { timestamp } = (await storage.get<CacheItem>(this.__r_hash_id)) ?? {}
    const staleTime = this.staleTime
    if (timestamp && staleTime !== -1 && Date.now() - timestamp > staleTime) {
      storage.del(this.__r_hash_id)
    }
  }

  /**
   * 清理缓存
   */
  __r_clearCache() {
    storage.clear()
  }

  /**
   * 获取缓存
   */
  async __r_getCache() {
    return await storage.get<CacheItem>(this.__r_hash_id)
  }

  async __r_initTrigger() {
    const item = await this.__r_getCache()
    if (item?.data) {
      this.data = item.data
      // 有缓存，并行请求刷新
      requestAnimationFrame(() => {
        this.__r_startTrigger()
      })
    } else {
      // 无缓存，阻塞刷新
      await this.__r_startTrigger()
    }
    return this.data
  }

  /**
   * 设置缓存
   */
  __r_setCache(data: any) {
    if (data === undefined) return
    const hashId = this.__r_hash_id
    const cacheTime = this.cacheTime
    const time = Date.now()
    if (cacheTime > -1) {
      clearTimeout(this.__r_cache_timer)
      this.__r_cache_timer = setTimeout(() => {
        storage.del(hashId)
      }, cacheTime)
    }
    storage.set(hashId, {
      data,
      timestamp: time
    })
  }

  /**
   * 开始轮询
   */
  __r_startPoll() {
    this.__r_poll_count ??= 0
    this.__r_poll_count++
    const pollCount = this.intervalCount ?? -1
    if (pollCount === -1 || this.__r_poll_count <= pollCount) {
      clearTimeout(this.__r_poll_timer)
      this.__r_poll_timer = setTimeout(() => {
        this.__r_startTrigger()
      }, this.intervalTime)
    } else {
      this.__r_poll_count = undefined
    }
  }

  /**
   * 开始重试
   */
  __r_startRetry() {
    this.__r_retry_count ??= 0
    this.__r_retry_count++
    const errorRetryCount = this.errorRetryCount ?? -1
    if (errorRetryCount === -1 || this.__r_retry_count <= errorRetryCount) {
      // Exponential backoff
      const timeout =
        this.errorRetryIntervalTime ??
        Math.min(1000 * 2 ** this.__r_retry_count, 30_000)
      clearTimeout(this.__r_retry_timer)
      this.__r_retry_timer = setTimeout(() => {
        this.__r_startTrigger()
      }, timeout)
    } else {
      this.__r_retry_count = undefined
    }
  }

  /**
   * 开始请求
   */
  async __r_startTrigger(triggerId?: number) {
    this.abort()
    this.__r_checkStale()
    if (!this.refreshOnOffline && this.__r_network_online === false)
      return this.data
    if (!this.refreshOnHidden && this.__r_window_visible === false)
      return this.data
    try {
      this.__r_abort_controller = new AbortController()
      clearTimeout(this.__r_timeout_timer)
      this.__r_timeout_timer = setTimeout(() => {
        this.abort()
      }, this.timeout)
      clearTimeout(this.__r_loading_debounce_timer)
      this.__r_loading_debounce_timer = setTimeout(() => {
        this.loading = true
      }, this.loadingDebounceTime)
      const result = await this.resolver(this.params, this.__r_abort_controller)
      const data =
        typeof this.transformer === 'function'
          ? this.transformer(result)
          : result
      // 只处理最后一次 trigger 的响应
      if (triggerId !== undefined && triggerId !== this.__r_trigger_version) {
        return
      }
      batch(() => {
        if (!isEqual(this.data, data)) {
          this.data = data
          this.updateAt = Date.now()
          this.__r_setCache(this.data)
        }
        this.error = null
        this.loading = false
        this.onSuccess?.()
      })
      if (this.intervalTime > 0) {
        this.__r_startPoll()
      }
      return data
    } catch (error) {
      // 只处理最后一次 trigger 的响应
      if (triggerId !== undefined && triggerId !== this.__r_trigger_version) {
        return
      }
      batch(() => {
        this.data = null
        this.updateAt = Date.now()
        this.error = error
        this.loading = false
        this.raise(error as Error)
        this.onError?.()
      })
      console.error(error)
      if (this.shouldRetryOnError) {
        this.__r_startRetry()
      }
    } finally {
      clearTimeout(this.__r_timeout_timer)
      clearTimeout(this.__r_loading_debounce_timer)
    }
  }

  /**
   * 中断请求
   */
  // @exposed()
  abort() {
    clearTimeout(this.__r_cache_timer)
    clearTimeout(this.__r_poll_timer)
    clearTimeout(this.__r_retry_timer)
    clearTimeout(this.__r_timeout_timer)
    clearTimeout(this.__r_loading_debounce_timer)
    this.__r_retry_count = 0
    this.__r_poll_count = 0
    if (this.__r_abort_controller) {
      this.__r_abort_controller.abort()
    }
  }

  getWatchDependencies() {
    const node = this.getTypedNode<QueryNode>()
    return toJSON([
      this.id,
      this.params,
      Object.fromEntries(
        node.mustaches.map((mustache) => [
          mustache.code,
          mustache.getWatchDependencies()
        ])
      )
    ])
  }

  /**
   * 重置所有数据
   */
  @exposed()
  @reactive('action')
  reset() {
    this.data = null
    this.error = null
    this.loading = false
    this.updateAt = null
  }

  /**
   * 自定义 Query 实现，必须重载
   * @param params
   * @param controller
   */
  @fromNode()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async resolver(params: TRecord | undefined, controller: AbortController) {}
}
